import { Auth } from 'aws-amplify';
import * as Sentry from '@sentry/browser';

import logger from '@/utils/logger';
import type {
  SocketChatResponse,
  SocketErrorResponse,
  SocketChunkResponse,
  SocketInsightResponse,
  SocketChatMessagePayload,
} from '@/types/chatbot.types';
import { SocketResponseType } from '@/types/chatbot.types';

let aiAssistantSocket: WebSocket | null = null;
let socketReconnectionInterval: NodeJS.Timeout | null = null;
let lastMessageReceivedDateTime: number | null = null;
let currentStationId: string | null = null;
let currentSiteDetails: { salesforceAccountId?: string; salesPersonName?: string } | null = null;
const SOCKET_RECONNECTION_INTERVAL_MILLIS = 1000 * 60;

type MessageHandler = (response: SocketChatResponse | SocketErrorResponse) => void;
type ConnectHandler = () => void;
type DisconnectHandler = () => void;

let messageHandler: MessageHandler | null = null;
let connectHandler: ConnectHandler | null = null;
let disconnectHandler: DisconnectHandler | null = null;

export const initAIAssistantSocket = async (
  stationId: string,
  onMessage: MessageHandler,
  onConnect?: ConnectHandler,
  onDisconnect?: DisconnectHandler,
  siteDetails?: { salesforceAccountId?: string; salesPersonName?: string },
): Promise<void> => {
  // If already connected to the same station, don't reconnect
  if (aiAssistantSocket && aiAssistantSocket.readyState === WebSocket.OPEN && currentStationId === stationId) {
    logger('AI Assistant socket already connected to this station');
    return;
  }

  try {
    messageHandler = onMessage;
    connectHandler = onConnect || null;
    disconnectHandler = onDisconnect || null;
    currentStationId = stationId;
    currentSiteDetails = siteDetails || null;

    if (socketReconnectionInterval) {
      clearInterval(socketReconnectionInterval);
      socketReconnectionInterval = null;
    }
    lastMessageReceivedDateTime = null;

    socketReconnectionInterval = setInterval(async () => {
      const now = new Date().getTime();
      if (lastMessageReceivedDateTime && now - lastMessageReceivedDateTime > SOCKET_RECONNECTION_INTERVAL_MILLIS) {
        logger('AI Assistant socket reconnection interval reached. Reconnecting...');
        await initAIAssistantSocket(stationId, onMessage, onConnect, onDisconnect, currentSiteDetails);
      }
    }, SOCKET_RECONNECTION_INTERVAL_MILLIS);

    const user = await Auth.currentAuthenticatedUser();
    const token = user.signInUserSession.idToken.jwtToken;

    // Close existing connection if switching stations
    if (aiAssistantSocket && currentStationId !== stationId) {
      closeAIAssistantSocket();
    }

    const socketBaseUrl = process.env.VITE_AI_AGENTS_WEBSOCKET_URL || 'wss://ai-agents-dev.ecoplant.co/v1';

    // Build URL with query parameters: Authorization, stationId, and optional site details
    const queryParams = new URLSearchParams({
      Authorization: token,
      stationId: stationId,
    });

    if (siteDetails?.salesforceAccountId) {
      queryParams.append('salesforceAccountId', siteDetails.salesforceAccountId);
    }

    if (siteDetails?.salesPersonName) {
      queryParams.append('salesPersonName', siteDetails.salesPersonName);
    }

    const socketUrl = `${socketBaseUrl}/?${queryParams.toString()}`;

    aiAssistantSocket = new WebSocket(socketUrl);

    aiAssistantSocket.onopen = function () {
      logger('AI Assistant socket connected');
      lastMessageReceivedDateTime = new Date().getTime();
      if (connectHandler) {
        connectHandler();
      }
    };

    aiAssistantSocket.onerror = function (e) {
      logger('Error on AI Assistant socket');
      Sentry.captureException(new Error(`AI Assistant socket connection failed - ${e}`));
    };

    aiAssistantSocket.onclose = function () {
      logger('AI Assistant socket closed');
      if (disconnectHandler) {
        disconnectHandler();
      }
    };

    aiAssistantSocket.onmessage = function (event) {
      lastMessageReceivedDateTime = new Date().getTime();
      handleSocketMessage(event);
    };
  } catch (e) {
    logger('Failed to initialize AI Assistant socket: ' + e);
    Sentry.captureException(new Error(`Failed to initialize AI Assistant socket - ${e}`));
  }
};

export const closeAIAssistantSocket = (): void => {
  if (socketReconnectionInterval) {
    clearInterval(socketReconnectionInterval);
    socketReconnectionInterval = null;
  }

  if (aiAssistantSocket) {
    aiAssistantSocket.close();
    aiAssistantSocket = null;
  }

  messageHandler = null;
  connectHandler = null;
  disconnectHandler = null;
  lastMessageReceivedDateTime = null;
  currentStationId = null;
  currentSiteDetails = null;
};

export const sendChatMessage = (
  sessionId: string,
  messageId: number,
  message: string,
  messageType = 'text',
  insightId?: string,
): void => {
  if (!aiAssistantSocket || aiAssistantSocket.readyState !== WebSocket.OPEN) {
    logger('AI Assistant socket is not connected. Cannot send message.');

    if (messageHandler) {
      const errorResponse: SocketErrorResponse = {
        eventName: 'chatError',
        payload: {
          conversationId: sessionId,
          error: 'Socket connection not available. Please ensure the backend is running.',
        },
      };
      messageHandler(errorResponse);
    }

    Sentry.captureException(new Error('Attempted to send message when socket is not connected'));
    return;
  }

  try {
    const payload: SocketChatMessagePayload = {
      sessionId,
      messageId,
      message,
      messageType,
      ...(insightId && { insightId }),
    };

    aiAssistantSocket.send(JSON.stringify(payload));
    logger(`Chat message sent via socket: ${message}`, { messageType, insightId });
  } catch (e) {
    logger('Failed to send chat message via socket: ' + e);
    Sentry.captureException(new Error(`Failed to send chat message via socket - ${e}`));
  }
};

const handleSocketMessage = (event: MessageEvent): void => {
  try {
    const data = JSON.parse(event.data);

    // Handle chunked free text responses (type: "text" or "error")
    if (data.type === SocketResponseType.TEXT || data.type === SocketResponseType.ERROR) {
      const chunk = data as SocketChunkResponse;
      if (messageHandler) {
        const response: SocketChatResponse = {
          eventName: 'chatResponse',
          payload: {
            conversationId: chunk.session_id,
            messageId: chunk.message_id.toString(),
            type: 'text', // Both text and error are chunked free text
            content: chunk.content,
            isComplete: chunk.is_final,
            sequence: chunk.sequence,
          },
        };
        messageHandler(response);
      } else {
        logger('No message handler registered for AI Assistant socket');
      }
      return;
    }

    // Handle incremental JSON chunks for insights (type: "insight")
    if (data.type === SocketResponseType.INSIGHT) {
      const insight = data as SocketInsightResponse;
      if (messageHandler) {
        const response: SocketChatResponse = {
          eventName: 'chatResponse',
          payload: {
            conversationId: insight.session_id,
            messageId: insight.message_id.toString(),
            type: 'insight',
            content: insight.content, // Incremental JSON object
            isComplete: insight.is_final,
          },
        };
        messageHandler(response);
      } else {
        logger('No message handler registered for AI Assistant socket');
      }
      return;
    }

    // Unknown response type
    logger('Unknown response type from AI Assistant socket:', data.type);
  } catch (e) {
    logger('Failed to parse AI Assistant socket message: ' + e);
    Sentry.captureException(new Error(`Failed to parse AI Assistant socket message - ${e}`));
  }
};
